import { useState } from "react";

/** Nothing to read: absent, blank, or an empty list. */
const isEmpty = (value: unknown): boolean =>
  value == null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

/**
 * Freezes the report's written copy for as long as the learner stays on one
 * step.
 *
 * Copy can be rewritten mid-read: generation finishes, the poll lands, and the
 * paragraph under the learner's eyes changes. Holding what the step opened
 * with keeps that from happening, whole rather than field by field, so a step
 * never shows half of one version and half of another. Anything landing
 * mid-step waits for the next one, a card that was not there before included.
 * Moving to another step, or back to one already seen, takes whatever is
 * current at that moment.
 *
 * Only copy goes through this. Scores, bands and benchmarks are data rather
 * than prose, they are read rather than being read *from*, and a learner
 * waiting on the query would otherwise be held on an empty screen.
 *
 * The step key is whatever identifies the step the learner is on: mobile names
 * its steps, this report numbers them.
 */
export default function useStepSnapshot<T>(
  stepKey: string | number,
  current: T
): T {
  const [snapshot, setSnapshot] = useState({ key: stepKey, value: current });

  if (snapshot.key !== stepKey) {
    // Adjusting state during render: React re-runs this component immediately
    // with the new snapshot rather than committing the stale one first.
    setSnapshot({ key: stepKey, value: current });
    return current;
  }

  // Nothing had arrived when the step opened, so there is nothing to protect.
  // The first copy to land becomes the snapshot and is held from there.
  if (isEmpty(snapshot.value) && !isEmpty(current)) {
    setSnapshot({ key: stepKey, value: current });
    return current;
  }

  return snapshot.value;
}
